import { useMemo, useCallback, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import AlertModal from '@components/ui/AlertModal/AlertModal';
import DataTable from '@components/shared/DataTable/DataTable';
import type { DataTableColumn, SortState } from '@components/shared/DataTable/DataTable';
import FilterPanel from '@components/shared/FilterPanel/FilterPanel';
import AppliedFiltersBar from '@components/shared/AppliedFiltersBar/AppliedFiltersBar';
import ProfileActionsMenu from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import type { ProfileAction } from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import { Forbidden } from '@workpay/ds-shared/components/Forbidden/Forbidden';
import { isForbiddenError } from '@workpay/ds-shared/components/Forbidden/ForbiddenBoundary';
import { WorkpayApiError } from '@lib/apiEnvelope';
import { dsEnvelopeService } from '@services/dsEnvelopeService';
import { useNotificationStore } from '@stores/notificationStore';
import { useAuthStore } from '@/stores/authStore';
import { useTableFilters } from '@/hooks/useTableFilters';
import { useTranslation } from '@hooks/useTranslation';
import { toPaginationState } from '@utils/tabHelpers';
import { toTitleCase } from '@utils/textCase';
import type { TableSearchParams } from '@utils/tabHelpers';
import type { FilterFieldConfig } from '@/types/filters';
import type { Envelope, EnvelopeStatus } from '@workpay/ds-shared/types/envelope';
import { EditAndResendModal, type RetriggerMode } from './EditAndResendModal';
import { AuditTrailModal } from '@components/shared/AuditTrailModal';
import { AUDIT_RESOURCE } from '@lib/auditResourceTypes';
import { useAuditTrigger } from '@/hooks/useAuditTrigger';
import styles from './SigningRequestList.module.scss';

const EDITABLE_STATUSES: EnvelopeStatus[] = [
  'DRAFT',
  'REVIEW_COMPLETED',
  'SENT',
  'VIEWED',
  'SIGNING_IN_PROGRESS',
  'REVISED',
  'PENDING_REVIEW',
  'UNDER_NEGOTIATION',
];

// DRAFT-equivalent statuses use ENVELOPES_UPDATE; the rest go through
// ENVELOPES_REVISE (Edit & Resend on a sent / reviewed request).
const DRAFT_LIKE_STATUSES: EnvelopeStatus[] = ['DRAFT', 'REVIEW_COMPLETED'];

interface SigningRequestListProps {
  routeTo: string;
  searchParams: TableSearchParams;
}

export function SigningRequestList({ routeTo, searchParams }: SigningRequestListProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const addNotification = useNotificationStore((s) => s.addNotification);

  const { page: urlPage, size: urlSize, sortBy, sortDir, filters: filtersParam } = searchParams;
  const page = urlPage ?? 0;
  const size = urlSize ?? 10;

  const sort: SortState | undefined = sortBy
    ? { columnId: sortBy, direction: (sortDir ?? 'desc') as 'asc' | 'desc' }
    : undefined;

  const filterFields = useMemo<FilterFieldConfig[]>(() => [
    {
      fieldKey: 'search',
      label: t('K_DS_COLUMN_SUBJECT', 'Subject'),
      type: 'text',
      placeholder: t('K_DS_PLACEHOLDER_SEARCH_SIGNING_REQUEST', 'Search by subject...'),
    },
    {
      fieldKey: 'status',
      label: t('K_COLUMN_STATUS', 'Status'),
      type: 'select',
      options: [
        { value: 'DRAFT', label: t('K_OPTION_DRAFT', 'Draft') },
        { value: 'SENT', label: t('K_DS_OPTION_SENT', 'Sent') },
        { value: 'COMPLETED', label: t('K_DS_OPTION_COMPLETED', 'Completed') },
        { value: 'VOIDED', label: t('K_DS_OPTION_VOIDED', 'Voided') },
        { value: 'EXPIRED', label: t('K_DS_OPTION_EXPIRED', 'Expired') },
      ],
    },
  ], [t]);

  const {
    filters,
    filterPayload,
    hasFilters,
    activeFilterCount,
    isPanelOpen,
    togglePanel,
    closePanel,
    applyFilters,
    removeFilter,
    clearAllFilters,
  } = useTableFilters({
    routeTo,
    filtersParam,
    fields: filterFields,
  });

  const sortString = useMemo(() => {
    if (!sort) return 'createdOn,desc';
    const key = sort.columnId === 'subject' ? 'subject' : sort.columnId === 'createdOn' ? 'createdOn' : sort.columnId;
    return `${key},${sort.direction}`;
  }, [sort]);

  const statusFilter = filterPayload?.filters?.status as string | undefined;

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['dsEnvelopes', { page, size, sort: sortString, statusFilter, filterPayload }],
    queryFn: () => dsEnvelopeService.getAll({
      page,
      size,
      sort: sortString,
      search: filterPayload?.search as string | undefined,
      status: statusFilter,
    }),
  });

  const signingRequests = data?.content ?? [];
  const pagination = useMemo(() => toPaginationState(data), [data]);
  const listForbidden = isForbiddenError(error);

  const [voidTarget, setVoidTarget] = useState<Envelope | null>(null);
  const [editTarget, setEditTarget] = useState<Envelope | null>(null);
  const audit = useAuditTrigger();
  // Stable opener — row-action deps shouldn't bust on modal open/close.
  const auditOpen = audit.open;

  const navigateToEdit = useCallback(
    (id: string, mode: RetriggerMode, startStep?: number) => {
      navigate({
        to: '/w/signing-requests/$requestId/edit',
        params: { requestId: id },
        search: startStep != null
          ? { retriggerMode: mode, startStep }
          : { retriggerMode: mode },
      });
    },
    [navigate],
  );

  const voidMutation = useMutation({
    mutationFn: (id: string) => dsEnvelopeService.void(id, 'Voided by sender'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dsEnvelopes'] });
      addNotification({ variant: 'success', title: t('K_DS_NOTIF_SIGNING_REQUEST_VOIDED', 'Signing request voided successfully') });
      setVoidTarget(null);
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  const getStatusBadgeClass = useCallback((status: string) => {
    switch (status) {
      case 'COMPLETED':
      case 'REVIEW_COMPLETED':
        return styles.badgeSuccess;
      case 'DRAFT':
        return styles.badgeWarning;
      case 'SENT':
      case 'VIEWED':
      case 'SIGNING_IN_PROGRESS':
      case 'PENDING_REVIEW':
      case 'UNDER_NEGOTIATION':
        return styles.badgeInfo;
      case 'VOIDED':
      case 'DECLINED':
      case 'EXPIRED':
        return styles.badgeNeutral;
      default:
        return styles.badgeNeutral;
    }
  }, []);

  const columns = useMemo<DataTableColumn<Envelope>[]>(() => [
    {
      id: 'subject',
      header: t('K_DS_COLUMN_SUBJECT', 'Subject'),
      sortable: true,
      sortKey: 'subject',
      width: '200px',
      cell: (item) => <span className={styles.cellPrimary} title={item.subject}>{item.subject}</span>,
    },
    {
      id: 'status',
      header: t('K_COLUMN_STATUS', 'Status'),
      cell: (item) => (
        <span className={`${styles.badge} ${getStatusBadgeClass(item.status)}`}>
          {toTitleCase(item.status.replace(/_/g, ' '))}
        </span>
      ),
    },
    {
      id: 'signingMethod',
      header: t('K_DS_COLUMN_SIGNING_METHOD', 'Signing Method'),
      cell: (item) => toTitleCase(item.signingMethod.replace(/_/g, ' ')),
    },
    {
      id: 'senderName',
      header: t('K_DS_COLUMN_SENDER', 'Sender'),
      cell: (item) => item.senderName || item.senderEmail,
    },
    {
      id: 'recipients',
      header: t('K_DS_COLUMN_RECIPIENTS', 'Recipients'),
      cell: (item) => item.recipients?.length ?? 0,
    },
    {
      id: 'createdOn',
      header: t('K_COLUMN_CREATED', 'Created'),
      sortable: true,
      sortKey: 'createdOn',
      cell: (item) => new Date(item.createdOn).toLocaleDateString(),
    },
  ], [t, getStatusBadgeClass]);

  const handleDownloadSigned = useCallback(
    async (item: Envelope) => {
      try {
        const docs = item.documents ?? [];
        for (const doc of docs) {
          if (!doc.id) continue;
          const response = await dsEnvelopeService.downloadSignedDocument(item.id, doc.id);
          const url = URL.createObjectURL(response.data);
          const a = document.createElement('a');
          a.href = url;
          a.download = doc.signedFileId ? `signed-${doc.name}` : doc.name || 'document.pdf';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }
      } catch {
        addNotification({
          variant: 'critical',
          title: t('K_DS_NOTIF_DOWNLOAD_FAILED', 'Failed to download signed documents'),
        });
      }
    },
    [addNotification, t],
  );

  const rowActions = useCallback(
    (item: Envelope) => {
      const actions: ProfileAction[] = [];

      actions.push({
        id: 'view',
        label: t('K_VIEW', 'View'),
        onClick: () =>
          navigate({ to: `${routeTo}/$requestId`, params: { requestId: item.id } }),
      });

      const isDraftLike = DRAFT_LIKE_STATUSES.includes(item.status);
      const editPerm = isDraftLike ? 'ENVELOPES_UPDATE' : 'ENVELOPES_REVISE';
      if (hasPermission(editPerm) && EDITABLE_STATUSES.includes(item.status)) {
        const hasSigned = item.recipients?.some((r) => r.status === 'SIGNED');
        // REVIEW_COMPLETED jumps the wizard straight to Place Fields (Step 4) —
        // user is finalising fields after the review parked the envelope.
        const startStep = item.status === 'REVIEW_COMPLETED' ? 4 : undefined;
        actions.push({
          id: 'edit',
          label: isDraftLike
            ? t('K_DS_ACTION_CONTINUE_EDITING', 'Continue Editing')
            : t('K_DS_ACTION_EDIT_AND_RESEND', 'Edit & Resend'),
          // Modal is only meaningful when someone has signed — that's the only
          // scenario where the author can pick `continue` vs `reset_all`. Skip
          // the modal otherwise and go straight to the wizard with `reset_all`.
          onClick: () => {
            if (hasSigned) {
              setEditTarget(item);
            } else {
              navigateToEdit(item.id, 'reset_all', startStep);
            }
          },
        });
      }

      if (item.status === 'COMPLETED' && hasPermission('ENVELOPES_DOWNLOAD')) {
        actions.push({
          id: 'download-signed',
          label: t('K_DS_ACTION_DOWNLOAD_SIGNED', 'Download Signed'),
          onClick: () => {
            void handleDownloadSigned(item);
          },
        });
      }

      if (
        hasPermission('ENVELOPES_VOID') &&
        item.status !== 'COMPLETED' &&
        item.status !== 'VOIDED'
      ) {
        actions.push({
          id: 'void',
          label: t('K_DS_ACTION_VOID', 'Void'),
          variant: 'default' as const,
          onClick: () => setVoidTarget(item),
        });
      }

      if (hasPermission('AUDIT_READ')) {
        actions.push({
          id: 'audit',
          label: t('K_AUDIT_TITLE', 'Audit trail'),
          onClick: () => auditOpen(item.id, item.subject || undefined),
        });
      }

      if (actions.length === 0) return null;
      return <ProfileActionsMenu actions={actions} />;
    },
    [t, navigate, routeTo, hasPermission, handleDownloadSigned, navigateToEdit, auditOpen],
  );

  const handleSortChange = useCallback((newSort: SortState | undefined) => {
    void navigate({
      to: routeTo,
      search: (prev: TableSearchParams) => ({
        ...prev,
        page: 0,
        sortBy: newSort?.columnId,
        sortDir: newSort?.direction,
      }),
      replace: true,
    });
  }, [navigate, routeTo]);

  const handlePageChange = useCallback((newPage: number) => {
    void navigate({
      to: routeTo,
      search: (prev: TableSearchParams) => ({ ...prev, page: newPage }),
      replace: true,
    });
  }, [navigate, routeTo]);

  const handlePageSizeChange = useCallback((newSize: number) => {
    void navigate({
      to: routeTo,
      search: (prev: TableSearchParams) => ({ ...prev, page: 0, size: newSize }),
      replace: true,
    });
  }, [navigate, routeTo]);

  const handleRowClick = useCallback((item: Envelope) => {
    navigate({ to: `${routeTo}/$requestId`, params: { requestId: item.id } });
  }, [navigate, routeTo]);

  const getRowKey = useCallback((item: Envelope) => item.id, []);

  const emptyState = useMemo(
    () =>
      listForbidden ? (
        <Forbidden variant="empty-state" hideAction />
      ) : (
        <div className={styles.emptyState}>
          <p>{t('K_DS_EMPTY_NO_SIGNING_REQUESTS', 'No signing requests yet')}</p>
          <p>{t('K_DS_EMPTY_GENERATE_FROM_EMPLOYEE', 'Generate a signing request from an employee profile to get started.')}</p>
        </div>
      ),
    [listForbidden, t],
  );

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerInfo}>
          <h1 className={styles.title}>{t('K_DS_PAGE_TITLE_SIGNING_REQUESTS', 'Signing Requests')}</h1>
          <p className={styles.subtitle}>{t('K_DS_PAGE_SUBTITLE_SIGNING_REQUESTS', 'Manage document signing requests')}</p>
        </div>
      </div>

      {hasFilters && (
        <AppliedFiltersBar
          fields={filterFields}
          values={filters}
          onRemove={removeFilter}
          onClearAll={clearAllFilters}
        />
      )}

      <DataTable
        columns={columns}
        data={signingRequests}
        rowKey={getRowKey}
        onRowClick={handleRowClick}
        loading={isLoading}
        fetching={isFetching}
        sort={sort}
        onSortChange={handleSortChange}
        pagination={pagination}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        showToolbar
        onFilterClick={togglePanel}
        activeFilterCount={activeFilterCount}
        rowActions={rowActions}
        emptyState={emptyState}
      />

      <FilterPanel
        isOpen={isPanelOpen}
        onClose={closePanel}
        fields={filterFields}
        values={filters}
        onApply={applyFilters}
        onReset={clearAllFilters}
        title={t('K_FILTER_TITLE', 'Filters')}
      />

      <AlertModal
        variant="danger"
        title={t('K_DS_MODAL_VOID_SIGNING_REQUEST', 'Void Signing Request')}
        description={t('K_DS_CONFIRM_VOID_SIGNING_REQUEST', 'Are you sure you want to void "{name}"? This action cannot be undone.').replace('{name}', voidTarget?.subject ?? '')}
        isOpen={!!voidTarget}
        onClose={() => setVoidTarget(null)}
        primaryLabel={t('K_DS_ACTION_VOID', 'Void')}
        onPrimaryClick={() => { if (voidTarget) voidMutation.mutate(voidTarget.id); }}
        loading={voidMutation.isPending}
      />

      <EditAndResendModal
        isOpen={!!editTarget}
        onClose={() => setEditTarget(null)}
        onConfirm={(mode) => {
          const target = editTarget;
          setEditTarget(null);
          if (target) navigateToEdit(target.id, mode);
        }}
      />

      <AuditTrailModal
        open={audit.target !== null}
        onClose={audit.close}
        filter={{ resourceType: AUDIT_RESOURCE.SIGNING_REQUEST, resourceId: audit.target?.id ?? '' }}
        subject={audit.target?.name}
      />
    </div>
  );
}
